<template>
    <div class="assignment-edit-header organizational-header">
        <h3 class="title 'order-1 order-sm-0">
            <div
                class="name"
                :class="inlineEditInProgress && 'editing'">
                <span class="assignment-title">
                    {{ assignmentIn.name }}
                    <b-button
                        class="btn-edit btn-cancel"
                        variant="primary-icon-only"
                        @click="inlineEditInProgress = true">
                    </b-button>
                </span>
                <div
                    class="inline-assignment-title-edit"
                    v-if="inlineEditInProgress">
                    <b-form-input v-model="title"></b-form-input>
                    <b-button
                        class="btn-checked btn-update"
                        variant="success-icon-only"
                        @click="updateAssignmentTitle">
                    </b-button>
                    <b-button
                        class="btn-times btn-cancel"
                        variant="primary-icon-only"
                        @click="cancelAssignmentTitleUpdate">
                    </b-button>
                </div>
            </div>
        </h3>

        <div class="assignment-edit-tools">
            <b-button
                class="btn-view-assignment"
                v-b-tooltip.hover
                @click="openAssignmentPreview"
                variant="primary-icon-only"
                :title="$t('assignment.view_assignment')">
                <span class="fa-layers fa-fw view-assignment">
                    <i class="fad fa-file file-icon"></i>
                    <i
                        class="fal fa-search loop-icon"
                        data-fa-transform="shrink-14">
                    </i>
                </span>
            </b-button>

            <div class="context-nav context-nav-main-assignment">
                <b-dropdown
                    class="ellipsis-nav assignment-nav"
                    no-caret
                    variant="primary"
                    right
                    toggle-class="assignment-edit-context-menu">
                    <template v-slot:button-content>
                        <i class="fas fa-ellipsis-v"></i>
                    </template>
                    <b-dropdown-item
                        class="btn-edit btn-edit-assignment-title"
                        @click="openAssignmentEdit">
                        {{ $t('assignment.change_name') }}
                    </b-dropdown-item>

                    <b-dropdown-item
                        class="btn-show-slides btn-open-select-lesson-modal"
                        @click="openSelectLessonModal">
                        {{ $t('assignment.select_lesson') }}
                    </b-dropdown-item>

                    <b-dropdown-item
                        class="btn-remove-assignment"
                        @click="remove">
                        <i class="btn-remove"></i>{{ $t('assignment.delete_assignment') }}
                    </b-dropdown-item>
                </b-dropdown>
            </div>
        </div>

        <design-2021-lesson-module-assignment-edit-modal-component
            ref="assignment-edit-modal"
            @assignment-updated="updateAssignment"
            :assignment-in="assignmentIn">
        </design-2021-lesson-module-assignment-edit-modal-component>

        <design-2021-assignment-edit-preview-modal-component
            ref="preview-modal"
            :assignment-in="assignmentIn">
        </design-2021-assignment-edit-preview-modal-component>

        <design-2021-assignment-edit-select-lesson-modal-component
            ref="selectLessonModal"
            @assignment-updated="updateAssignment"
            :assignment-in="assignmentIn">
        </design-2021-assignment-edit-select-lesson-modal-component>
    </div>
</template>

<script>
export default {
    name: 'design-2021-lesson-module-assignment-edit-title-buttons-component',
    props: {
        assignmentIn: {
            type: Object,
            required: true,
        },
        lessonModuleIn: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            inlineEditInProgress: false,
            title: this.assignmentIn.name,
        };
    },
    methods: {
        openAssignmentPreview() {
            this.$refs['preview-modal'].show();
        },
        openAssignmentEdit() {
            this.$refs['assignment-edit-modal'].onShow();
        },
        updateAssignment(newAssignment) {
            this.$emit('assignment-updated', newAssignment);
        },
        remove() {
            this.alert
                .confirm(this.$t('assignment.delete_assignment'), this.$t('assignment.delete_assignment_description'))
                .then((confirm) => {
                    if (confirm) {
                        this.alert.saving();

                        this.axios.delete(this.routes.assignmentDelete(this.assignmentIn.id)).then(
                            (response) => {
                                this.alert.success();
                                window.location.href = this.routes.lessonModuleShow(this.lessonModuleIn.id);
                            },
                            (error) => {
                                this.alert.ajaxError(error);
                            },
                        );
                    }
                });
        },

        openSelectLessonModal() {
            this.$refs.selectLessonModal.show();
        },

        updateAssignmentTitle() {
            if (this.title === null || this.title === '') {
                this.alert.error(
                    this.$t('assignment.title_edit_error_title'),
                    this.$t('assignment.title_edit_error_description'),
                );
                return;
            }

            this.alert.notice();

            const data = {
                name: this.title,
            };

            this.axios.post(this.routes.assignmentUpdate(this.assignmentIn.id), data).then(
                (response) => {
                    this.inlineEditInProgress = false;
                    this.$emit('assignment-updated', response.data);
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        cancelAssignmentTitleUpdate() {
            this.title = this.assignmentIn.name;
            this.inlineEditInProgress = false;
        },
    },
};
</script>

<style scoped lang="scss">
.assignment-edit-header {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: wrap;
    flex: 1 0 100%;

    margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');

    .title {
        font-weight: $font-weight-bold;
        margin-bottom: 0;

        .name {
            display: flex;
            flex-direction: column;
            flex-wrap: wrap;
            align-items: flex-start;
            justify-content: flex-start;
            column-gap: $grid-gutter-width;
            row-gap: map-get($grid-gutter-spacing, 'four');

            &.editing {
                .assignment-title {
                    display: none;
                    opacity: 0;
                    width: 0;
                    height: 0;
                    left: -100%;
                }

                .inline-assignment-title-edit {
                    opacity: 1;
                }
            }
        }

        .deadline {
            margin-top: map-get($grid-gutter-spacing, 'eight');
        }
    }

    .assignment-title,
    .inline-assignment-title-edit {
        display: flex;
        align-items: center;
        transition: all 0.8s ease;
    }

    .assignment-title {
        position: relative;
        column-gap: map-get($grid-gutter-spacing, 'fifteen');
        opacity: 1;
        width: auto;
        height: auto;

        .btn-update-title {
            &:before {
                margin: 0;
            }
        }
    }

    .inline-assignment-title-edit {
        column-gap: map-get($grid-gutter-spacing, 'eight');
        opacity: 0;
        width: 100%;

        input {
            margin: 0;
            border: 1px solid $primary;
            padding: map-get($grid-gutter-spacing, 'ten') map-get($grid-gutter-spacing, 'fifteen');
        }

        .btn {
            font-size: 1.125rem;

            &:before {
                margin: 0;
            }
        }
    }

    .deadline {
        color: $danger;
        font-size: $base-font-size;

        .icon {
            margin-right: map-get($grid-gutter-spacing, 'eight');
        }
    }

    .assignment-edit-tools {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-end;
        flex: 1 0 100%;
        column-gap: map-get($grid-gutter-spacing, 'fifteen');

        .group-pair-status {
            .icon {
                color: var(--primary);
            }
        }

        .deadline {
            margin-right: auto;
        }
    }

    .assignment-rating-status {
        font-size: 1.3125rem;

        &:before {
            margin-right: 0;
        }

        &.btn-warning-icon:hover {
            background-color: $warning;
        }

        &.btn-warning-icon:hover {
            background-color: $warning;
        }

        &:hover {
            cursor: pointer;
        }
    }

    .view-assignment {
        position: relative;
        font-size: 2.0625rem;
        line-height: 1;
        height: 40px;
        width: 36px;

        .file-icon {
            font-size: 2.5rem;
            color: var(--primary);
        }

        .loop-icon {
            position: absolute;
            display: block;
            top: 13px;
            left: 5px;
            color: $white;
            font-size: 1.25rem;
        }
    }

    .context-nav-main-assignment ::v-deep {
        .assignment-nav {
            button.dropdown-toggle {
                color: $white !important;
                background-color: var(--primary);
                padding: map-get($grid-gutter-spacing, 'eight') map-get($grid-gutter-spacing, 'fifteen');
            }

            .btn-eye,
            .btn-eye-closed,
            .btn-assignment-rating-settings,
            .btn-assignment-cooperation,
            .btn-assignment-deadline,
            .btn-edit-assignment-title,
            .update-selected-tag {
                i,
                &:before {
                    color: var(--primary);
                }
            }

            .btn-assignment-cooperation,
            .btn-assignment-deadline,
            .btn-remove-assignment,
            .btn-edit-assignment-title {
                font-size: $default-font-size;

                .dropdown-item {
                    display: flex;
                    align-items: center;

                    i {
                        margin-right: map-get($grid-gutter-spacing, 'eight');
                        font-size: $base-font-size;
                    }
                }

                &:before {
                    margin-right: map-get($grid-gutter-spacing, 'eight');
                }
            }

            .dropdown-menu {
                border: 1px solid $border-color;

                li {
                    &:before,
                    i {
                        min-width: 20px;
                    }
                }
            }
        }
    }
}

.organizational-header {
    .title {
        width: calc(100% - 51px);
        align-self: center;
    }

    .organizational-tools {
        align-self: baseline;
        display: flex;
        align-items: center;
        margin-left: auto;
    }
}

@media (min-width: map-get($grid-breakpoints, 'sm')) {
    .assignment-edit-header {
        flex-wrap: nowrap;

        .title {
            width: calc(100% - 95px);
        }

        .assignment-edit-tools {
            flex: 0 1 auto;
            margin-left: auto;
            align-self: baseline;
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .inline-assignment-title-edit {
        width: 100%;
    }
}
</style>
